import {
  AccessTime,
  CheckCircleOutline,
  ErrorOutline,
  FiberManualRecord,
  HourglassEmpty,
  InfoOutlined,
  PendingActions,
} from '@mui/icons-material';
import { Box, Theme, Tooltip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import moment from 'moment';

import { PayoutTimelineEvent } from './types';

interface TimelineProps {
  events: PayoutTimelineEvent[];
}

type TimelineVisualState = 'pending' | 'inProgress' | 'error' | 'completed' | 'default';

const useStyles = makeStyles()((theme: Theme) => ({
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '14px',
    marginTop: '12px',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '24px 1fr',
    columnGap: '10px',
    alignItems: 'stretch',
  },
  iconColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  line: {
    width: '2px',
    flexGrow: 1,
    minHeight: '14px',
    backgroundColor: theme.palette.secondary.dark,
    marginTop: '4px',
  },
  title: {
    color: theme.palette.common.white,
    fontFamily: 'Inter',
    fontSize: '14px',
    fontWeight: 600,
  },
  titleRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  hintIcon: {
    color: 'rgba(255, 255, 255, 0.7)',
    fontSize: '16px',
  },
  timestamp: {
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '12px',
    marginTop: '2px',
  },
  metadata: {
    color: theme.palette.text.secondary,
    fontFamily: 'Inter',
    fontSize: '12px',
    marginTop: '4px',
    whiteSpace: 'normal',
  },
  pendingTitle: {
    color: '#7b7e83',
  },
  pendingTimestamp: {
    color: '#7b7e83',
  },
  pendingMetadata: {
    color: '#7b7e83',
  },
}));

const getTimelineVisualState = (event: PayoutTimelineEvent): TimelineVisualState => {
  const statusValue = (event.status || '').toLowerCase();
  const value = `${event.status || ''} ${event.description || ''} ${event.metadata || ''} ${event.title}`.toLowerCase();

  // Prefer explicit backend status first.
  if (
    statusValue.includes('failed') ||
    statusValue.includes('cancel') ||
    statusValue.includes('stopped') ||
    statusValue.includes('stop payment') ||
    statusValue.includes('error') ||
    statusValue.includes('expired')
  ) {
    return 'error';
  }

  if (
    statusValue.includes('done') ||
    statusValue.includes('completed') ||
    statusValue.includes('paid') ||
    statusValue.includes('cleared') ||
    statusValue.includes('delivered') ||
    statusValue.includes('deposited')
  ) {
    return 'completed';
  }

  if (
    statusValue.includes('not started') ||
    statusValue.includes('pending') ||
    value.includes('not started') ||
    value.includes('pending')
  ) {
    return 'pending';
  }

  if (
    statusValue.includes('in progress') ||
    statusValue.includes('processing') ||
    statusValue.includes('initiated') ||
    (statusValue.includes('started') && !statusValue.includes('not started')) ||
    value.includes('in progress') ||
    value.includes('processing') ||
    value.includes('initiated') ||
    (value.includes('started') && !value.includes('not started'))
  ) {
    return 'inProgress';
  }

  // Fallback to keyword heuristics when explicit status is unavailable.
  if (
    value.includes('failed') ||
    value.includes('cancel') ||
    value.includes('stopped') ||
    value.includes('stop payment') ||
    value.includes('error') ||
    value.includes('expired')
  ) {
    return 'error';
  }

  if (
    value.includes('done') ||
    value.includes('completed') ||
    value.includes('paid') ||
    value.includes('cleared') ||
    value.includes('delivered') ||
    value.includes('deposited')
  ) {
    return 'completed';
  }

  return 'default';
};

const iconByStatus = (event: PayoutTimelineEvent) => {
  if (event.icon === 'greenDot') {
    return <FiberManualRecord fontSize="small" htmlColor="#88B856" />;
  }

  const visualState = getTimelineVisualState(event);

  if (visualState === 'pending') {
    return <HourglassEmpty fontSize="small" htmlColor="#C3C4C8" />;
  }

  if (visualState === 'inProgress') {
    return <PendingActions fontSize="small" htmlColor="#FFD900" />;
  }

  if (visualState === 'error') {
    return <ErrorOutline fontSize="small" htmlColor="#FF8663" />;
  }

  if (visualState === 'completed') {
    return <CheckCircleOutline fontSize="small" htmlColor="#88B856" />;
  }

  return <AccessTime fontSize="small" htmlColor="#C3C4C8" />;
};

const Timeline = ({ events }: TimelineProps) => {
  const { classes } = useStyles();

  return (
    <Box className={classes.timeline} aria-label="Onboarding history timeline">
      {events.map((event, index) => {
        const visualState = getTimelineVisualState(event);

        return (
        <Box key={`${event.title}-${event.timestamp || index}`} className={classes.row}>
          <Box className={classes.iconColumn}>
            {iconByStatus(event)}
            {index < events.length - 1 && <Box className={classes.line} />}
          </Box>
          <Box>
            <Box className={classes.titleRow}>
              <Typography
                className={`${classes.title} ${visualState === 'pending' ? classes.pendingTitle : ''}`}
              >
                {event.title}
              </Typography>
              {!!event.claimantStep && (
                <Tooltip
                  title={`Claimant Portal Status: ${event.claimantStep}`}
                  arrow
                  placement="top-start"
                  componentsProps={{
                    tooltip: {
                      sx: (theme) => ({
                        backgroundColor: theme.palette.common.white,
                        color: theme.palette.primary.light,
                        borderRadius: '5px',
                        boxShadow: '3px',
                        fontFamily: 'Inter',
                        fontSize: '13px',
                        fontWeight: 500,
                        padding: '5px',
                      }),
                    },
                    arrow: {
                      sx: (theme) => ({
                        color: theme.palette.common.white,
                      }),
                    },
                  }}
                >
                  <InfoOutlined className={classes.hintIcon} />
                </Tooltip>
              )}
            </Box>
            <Typography
              className={`${classes.timestamp} ${visualState === 'pending' ? classes.pendingTimestamp : ''}`}
            >
              {event.timestamp ? moment(event.timestamp).format('MMM D, YYYY h:mm A') : 'No timestamp'}
            </Typography>
            {(event.metadata || event.description) && (
              <Typography
                className={`${classes.metadata} ${visualState === 'pending' ? classes.pendingMetadata : ''}`}
              >
                {event.metadata || event.description}
              </Typography>
            )}
          </Box>
        </Box>
        );
      })}
    </Box>
  );
};

export default Timeline;
